import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LoadingPage } from "@/components/ui/loading-spinner";
import { EmptyState } from "@/components/ui/empty-state";
import { BookOpen, Search, TrendingUp, DollarSign, GraduationCap, Briefcase, ArrowRight, Clock, BarChart, Heart } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const Careers = () => {
  const { token } = useContext(AuthContext);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [careers, setCareers] = useState([]);
  const [filteredCareers, setFilteredCareers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");

  useEffect(() => {
    fetchCareers();
  }, []);

  useEffect(() => {
    filterCareers();
  }, [searchTerm, selectedCategory, careers]);

  const fetchCareers = async () => {
    try {
      const response = await axios.get(`${API}/careers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCareers(response.data);
      setFilteredCareers(response.data);
    } catch (error) {
      toast.error("Failed to load careers");
    } finally {
      setLoading(false);
    }
  };

  const filterCareers = () => {
    let filtered = careers;

    if (searchTerm) {
      filtered = filtered.filter(
        (career) =>
          career.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          career.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory !== "All") {
      filtered = filtered.filter((career) => career.category === selectedCategory);
    }

    setFilteredCareers(filtered);
  };

  const categories = ["All", ...new Set(careers.map((c) => c.category))];

  const getCategoryColor = (category) => {
    const colors = {
      Technology: "bg-primary/10 text-primary border-primary/20",
      Healthcare: "bg-success/10 text-success border-success/20",
      Creative: "bg-purple-100 text-purple-700 border-purple-200",
      Business: "bg-warning/10 text-warning border-warning/20",
      Education: "bg-pink-100 text-pink-700 border-pink-200",
      Engineering: "bg-blue-100 text-blue-700 border-blue-200",
      Legal: "bg-indigo-100 text-indigo-700 border-indigo-200",
      Government: "bg-teal-100 text-teal-700 border-teal-200",
      Hospitality: "bg-orange-100 text-orange-700 border-orange-200",
    };
    return colors[category] || "bg-muted text-muted-foreground border-border";
  };

  const getWorkLifeColor = (balance) => {
    const colors = {
      'Very Good': 'bg-emerald-100 text-emerald-700 border-emerald-200',
      'Good': 'bg-green-100 text-green-700 border-green-200',
      'Moderate': 'bg-amber-100 text-amber-700 border-amber-200',
      'Challenging': 'bg-red-100 text-red-700 border-red-200'
    };
    return colors[balance] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getGrowthColor = (potential) => {
    const colors = {
      'Very High': 'bg-purple-100 text-purple-700 border-purple-200',
      'High': 'bg-blue-100 text-blue-700 border-blue-200',
      'Moderate': 'bg-yellow-100 text-yellow-700 border-yellow-200',
      'Limited': 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return colors[potential] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <Navbar />
        <LoadingPage message="Loading careers..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8" data-testid="careers-container">
        {/* Compact Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md">
              <Briefcase className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                {t('careers.title')}
              </h1>
              <p className="text-gray-600 text-sm md:text-base">
                {t('careers.subtitle')}
              </p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="mb-8 border-none shadow-lg">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-5 h-5" />
                <Input
                  type="text"
                  placeholder={t('careers.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 h-12 text-base border-none bg-muted/30"
                  data-testid="career-search-input"
                />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap mt-4">
              {categories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className={selectedCategory === category ? "shadow-md" : ""}
                  data-testid={`category-${category.toLowerCase()}`}
                >
                  {category}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Results Count */}
        {filteredCareers.length > 0 && (
          <div className="mb-6">
            <p className="text-muted-foreground">
              Showing <span className="font-semibold text-foreground">{filteredCareers.length}</span> career{filteredCareers.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}

        {/* Careers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {filteredCareers.map((career) => (
            <Card
              key={career.id}
              className="group border-none shadow-md hover:shadow-2xl cursor-pointer transition-all duration-300 overflow-hidden"
              onClick={() => navigate(`/careers/${career.id}`)}
              data-testid={`career-card-${career.id}`}
            >
              {/* Gradient Top Bar */}
              <div className="h-2 bg-gradient-to-r from-primary via-primary/80 to-primary/60"></div>
              
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between mb-3">
                  <Badge variant="outline" className={`${getCategoryColor(career.category)} text-xs font-medium`}>
                    {career.category}
                  </Badge>
                </div>
                <CardTitle className="text-xl mb-2 group-hover:text-primary transition-colors">
                  {career.title}
                </CardTitle>
                <CardDescription className="line-clamp-2 text-sm leading-relaxed">
                  {career.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-3 pt-0">
                {/* Salary Ranges */}
                {career.salary_range_india && (
                  <div className="space-y-2 pb-3 border-b">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                        <DollarSign className="w-4 h-4 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500">India</p>
                        <p className="font-semibold text-sm text-green-700">{career.salary_range_india}</p>
                      </div>
                    </div>
                    {career.salary_range_global && (
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <DollarSign className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500">Global</p>
                          <p className="font-semibold text-sm text-blue-700">{career.salary_range_global}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  {/* Job Outlook */}
                  {career.job_outlook && (
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <TrendingUp className="w-4 h-4 text-primary" />
                      </div>
                      <span className="font-medium">{career.job_outlook}</span>
                    </div>
                  )}
                  
                  {/* Timeline */}
                  {career.timeline_to_enter && (
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-4 h-4 text-amber-600" />
                      </div>
                      <span className="font-medium">{career.timeline_to_enter}</span>
                    </div>
                  )}
                  
                  {/* Education */}
                  {career.required_education && career.required_education.length > 0 && (
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <div className="w-8 h-8 rounded-lg bg-warning/10 flex items-center justify-center flex-shrink-0">
                        <GraduationCap className="w-4 h-4 text-warning" />
                      </div>
                      <span className="font-medium">{career.required_education[0]}</span>
                    </div>
                  )}
                </div>
                
                {/* Work-Life Balance & Growth */}
                <div className="flex gap-2 pt-2">
                  {career.work_life_balance && (
                    <Badge variant="outline" className={`${getWorkLifeColor(career.work_life_balance)} text-xs flex-1 justify-center`}>
                      <Heart className="w-3 h-3 mr-1" />
                      {career.work_life_balance}
                    </Badge>
                  )}
                  {career.growth_potential && (
                    <Badge variant="outline" className={`${getGrowthColor(career.growth_potential)} text-xs flex-1 justify-center`}>
                      <BarChart className="w-3 h-3 mr-1" />
                      {career.growth_potential}
                    </Badge>
                  )}
                </div>

                {/* View Details Button */}
                <Button 
                  variant="ghost" 
                  className="w-full mt-4 group-hover:bg-primary group-hover:text-white transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/careers/${career.id}`);
                  }}
                >
                  View Details
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {filteredCareers.length === 0 && (
          <EmptyState
            icon={BookOpen}
            title="No careers found"
            description={searchTerm ? "Try adjusting your search or filter" : "No careers available yet"}
          />
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Careers;
